'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { SettingToggleRow } from '@kit/ui/setting-toggle-row';
import { Trans } from '@kit/ui/trans';
import {
  MediaPicker,
  type MediaPickerLibraryItem,
  type UploadResult,
} from '@kit/uploads';

import type { UpdateAuditionInput } from '../../schemas/audition.schema';

interface CompetitionPublicTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
  /**
   * The organization's media library (photos + video) + org id. Feeds the cover
   * MediaPicker. `null` when the account has no linked organization.
   */
  orgMedia?: { organizationId: string; items: MediaPickerLibraryItem[] } | null;
  /** Cover upload action injected from the app layer (see AuditionForm). */
  coverUploadAction?: (formData: FormData) => Promise<UploadResult>;
}

/**
 * Competition branding / public-page / livestream sections — competition-only.
 *
 * Rendered inside the Basic info tab (BAL — branding rework 2026-07-02); the
 * former standalone "Public page & branding" tab was folded in. Edits the same
 * `UpdateAuditionInput` shape over the same `updateAuditionAction` write path as
 * every other section (Critical Rule #8 — single edit surface).
 *
 * The cover is picked from the ORGANIZATION's media library via the ready-made
 * `@kit/uploads` MediaPicker and stored as `competitionBranding.cover_media_id`
 * (the write path derives the public `cover_image_url` consumers read). There is
 * no per-competition name (the public title is `audition.title`) and no
 * per-competition logo (public surfaces always render the organization's logo).
 */
export function CompetitionPublicTab({
  form,
  orgMedia,
  coverUploadAction,
}: CompetitionPublicTabProps) {
  const { t } = useTranslation('auditions');

  return (
    <div className="space-y-6">
      {/* Branding */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:editor.competitionPublic.branding.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:editor.competitionPublic.branding.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="competitionBranding.cover_media_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:editor.competitionPublic.fields.cover.label" />
                </FormLabel>
                <FormControl>
                  <MediaPicker
                    types={['photo', 'video']}
                    mode="single"
                    value={field.value || null}
                    onChange={(v) =>
                      field.onChange(typeof v === 'string' ? v : '')
                    }
                    mediaTypeForUpload="photo"
                    loadLibrary={async () => orgMedia?.items ?? []}
                    selectedItemsHint={orgMedia?.items ?? []}
                    uploadAction={
                      coverUploadAction ??
                      (async () => ({
                        success: false,
                        error: t(
                          'editor.competitionPublic.fields.cover.uploadUnavailable',
                        ),
                      }))
                    }
                    translations={{
                      triggerLabel: t(
                        'editor.competitionPublic.fields.cover.pickLabel',
                      ),
                      triggerLabelHasSelection: t(
                        'editor.competitionPublic.fields.cover.replaceLabel',
                      ),
                    }}
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:editor.competitionPublic.fields.cover.description" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="competitionBranding.accent_color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.accentColor.label" />
                  </FormLabel>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Input
                        type="color"
                        className="h-10 w-14 cursor-pointer p-1"
                        {...field}
                        value={field.value || '#000000'}
                      />
                    </FormControl>
                    <Input
                      placeholder="#1a1a2e"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="competitionBranding.website_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.website.label" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://globaldanceopen.com"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Public page visibility */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:editor.competitionPublic.visibility.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:editor.competitionPublic.visibility.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            <FormField
              control={form.control}
              name="hideApplyButton"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideApply.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideApply.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hideIndividualParticipants"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideDancers.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideDancers.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hideOrganizationParticipants"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideOrgs.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.hideOrgs.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            {/* US-128: GDO partner-visibility gate. Off until the admin reveals
                the participants to competition partners (e.g. after the
                classical/contemporary evaluation round), so partners see dancers
                fresh on stage first. Enforced in RLS, not just here. */}
            <FormField
              control={form.control}
              name="participantsVisibleToPartners"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.participantsVisibleToPartners.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionPublic.fields.participantsVisibleToPartners.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Livestream & support (US-134, US-135) */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:editor.competitionPublic.livestream.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:editor.competitionPublic.livestream.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="competitionBranding.livestream_embed_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:editor.competitionPublic.fields.livestream.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://www.youtube.com/watch?v=…"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:editor.competitionPublic.fields.livestream.description" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="competitionBranding.support_button_label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.supportLabel.label" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'editor.competitionPublic.fields.supportLabel.placeholder',
                      )}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.supportLabel.description" />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="competitionBranding.support_button_destination"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.supportDestination.label" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="mailto:hello@gdo.com or https://wa.me/…"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription>
                    <Trans i18nKey="auditions:editor.competitionPublic.fields.supportDestination.description" />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
